import React, { useEffect, useRef, useState } from "react";
import { Formik, Form, Field } from "formik";
import Modal from "../components/Modal/Modal";
import { hideModal, showModal } from "../redux/modalStore";
import { useDispatch, useSelector } from "react-redux";
import DatePickerField from "../components/FormikComponents/DatePickerField";
import Textarea from "../components/Textarea/Textarea";
import styles from "./NewFindingModal.module.scss";
import Button from "../components/Button/Button";
import Input from "../components/Input/Input";
import FormikCheckbox from "../components/FormikComponents/FormikCheckBox/FormikCheckBox";
import FormikUpload from "../components/FormikComponents/FormikUpload/FormikUpload";
import _ from "lodash";
import axiosInstance from "../utils/utils";
import { useNavigate, useParams } from "react-router-dom";
import { getOrganizationData } from "../actions/organization";
import * as Yup from "yup";
import store from "../core/store";
import { setLinkedData } from "../redux/findingsStore";
import ConfirmationPopup from "./ConfirmationPopup";
import classNames from "classnames";
import { ReactComponent as LockIcon } from "../icons/lock.svg";
import WarningIllustration from "../icons/no-data.svg";
import CascadingDropdown from "../components/CascadingDropdown/CascadingDropdown";
import { Tooltip } from "react-tooltip";

const NewFindingModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [prefilledChapterFromAPI, setPrefilledChapterFromAPI] = useState("");
  const [checklist, setChecklist] = useState({});
  const formikRef = useRef(null); // Ref to access Formik's setFieldValue

  const { risksData, auditsTasksData, linkedData, findingsData } = useSelector(
    (state) => state.organization,
  );
  const openedModal = useSelector((state) => state.modal.openedModal);

  const { orgId } = useParams();
  const sessionToken = localStorage.getItem("session_token");

  const organizationData = useSelector(
    (state) => state.user.organizations[orgId],
  );

  const risksArray = Object.values(risksData);
  const sortedRisks = _.sortBy(risksArray, "risk_id");

  const isFromAuditWithPrefilledRisk =
    !!openedModal?.data?.risk?.risk_id && !!openedModal?.data?.risk?.qms_id;

  const riskOptions = sortedRisks.map((risk) => ({
    id: risk.risk_num,
    reference: risk.risk_id,
    label: `${risk.risk_id} ${risk.description}`,
  }));

  let chapterOptions = sortedRisks.map((risk) => ({
    id: risk.qms_id,
    label: risk.qms_id + " " + risk.qms_description,
  }));

  chapterOptions = _.uniqBy(chapterOptions, "id");

  const cascadingRiskOptions = sortedRisks
    .filter((risk) => risk.level?.toLowerCase() !== "none")
    .map((risk) => ({
      id: risk.risk_num,
      reference: risk.risk_id,
      label: `${risk.risk_id} ${risk.description}`,
    }));

  const validChapterIds = new Set(
    cascadingRiskOptions.map((risk) => risk.reference.split("-")[0]),
  );

  const cascadingChapterOptions = Array.from(validChapterIds).map(
    (chapterId) => {
      const matchingRisk = sortedRisks.find(
        (risk) => risk.qms_id === chapterId,
      );
      return {
        id: chapterId,
        label: `${chapterId} ${matchingRisk?.qms_description || ""}`,
      };
    },
  );

  const prefilledRiskNum = openedModal?.data?.risk?.risk_num || "";

  const validationSchema = Yup.object().shape({
    description: Yup.string().required("Please provide a description."),
    reference: Yup.string().required("Reference cannot be empty."),
    risk: Yup.string()
      .oneOf(
        cascadingRiskOptions.map((opt) => String(opt.id)),
        "Please select a valid risk.",
      )
      .required("Please select an associated risk."),
    attachments: Yup.array()
      .of(Yup.string())
      .required("File upload is required"),
  });

  const handleCloseModal = (dirty, resetForm) => {
    // Check if there are unsaved changes
    if (dirty) {
      setShowConfirmationPopup(true); // Show confirmation if there are unsaved changes
    } else {
      // If there are no unsaved changes, check if opened from the audits modal
      if (openedModal?.data?.id) {
        // Close the new finding modal and reopen the audits modal
        dispatch(hideModal()); // Close the new finding modal

        // Ensure that the id is always at least 4 digits
        const auditId = String(openedModal.data.id).padStart(4, "0"); // For example, pads '768' to '0768'
        const formattedKey = `A-${auditId}`;

        // Reopen the audits modal with the previous data
        const updatedAuditData =
          store.getState().organization.auditsTasksData[formattedKey];

        if (updatedAuditData) {
          reopenAuditModal(updatedAuditData); // Reopen the audits modal
        }
      } else {
        // Otherwise, just close the modal without reopening anything
        resetForm();
        dispatch(hideModal());
        setChecklist({});
        setPrefilledChapterFromAPI("");
      }
    }
  };

  const handleConfirmClose = (resetForm) => {
    setShowConfirmationPopup(false);

    if (openedModal?.data?.id) {
      // Close the new finding modal and reopen the audits modal
      resetForm();
      dispatch(hideModal()); // Close the new finding modal

      // Reopen the audits modal with the previous data
      const updatedAuditData =
        store.getState().organization.auditsTasksData[
          `A-${openedModal.data.id}`
        ];

      setTimeout(() => {
        if (updatedAuditData) {
          reopenAuditModal(updatedAuditData); // Reopen the audits modal with the previous data
        }
      }, 200); // Delay to allow the Redux store to update
    } else {
      // Else, just close the modal
      resetForm(); // Reset form values
      dispatch(hideModal());
      setChecklist({});
      setPrefilledChapterFromAPI("");
    }
  };

  const handleCancel = () => {
    setShowConfirmationPopup(false);
  };

  const formatDate = (inputDate) => {
    if (!inputDate) {
      return new Date().toISOString().split("T")[0];
    }
    return new Date(inputDate).toISOString().split("T")[0];
  };

  // Function to reopen the audit modal with a timeout
  const reopenAuditModal = (updatedAuditData) => {
    setTimeout(() => {
      dispatch(
        showModal({
          name: "audits_modal",
          data: updatedAuditData, // Reopen the audit modal with the previous data
        }),
      );
    }, 100);
  };

  const handleSubmit = async (values, actions) => {
    const selectedRisk = cascadingRiskOptions.find(
      (risk) => String(risk.id) === String(values.risk),
    );
    const riskNumToSend = selectedRisk ? selectedRisk.id : null;

    if (!riskNumToSend) {
      actions.setSubmitting(false);
      setLoading(false);
      return;
    }

    const { chapter, ...rest } = values;

    const valuesToSend = {
      ...rest,
      date: formatDate(values.date),
      risk: riskNumToSend,
    };

    const options = {
      method: "POST",
      url: `/nocodb/findings?org_id=${orgId}`,
      headers: { "session-token": sessionToken },
      data: [valuesToSend],
    };

    try {
      const response = await axiosInstance.request(options);
      const findingId = response.data[0]; // Get the new finding ID

      // Check if openedModal?.data?.id exists
      if (openedModal?.data?.id) {
        const linkOptions = {
          method: "POST",
          url: `/nocodb/link_findings?org_id=${orgId}&link_target=AuditTask&target_id=${openedModal.data.id}`,
          headers: { "session-token": sessionToken },
          data: [findingId],
        };

        await axiosInstance.request(linkOptions);

        // Call getOrganizationData to refresh the organization data
        await dispatch(
          getOrganizationData(
            orgId,
            sessionToken,
            navigate,
            false,
            true,
            false,
            true,
            false,
            false,
          ),
        );

        setTimeout(() => {
          // After refreshing organization data, fetch the updated audit data
          const updatedAuditData =
            store.getState().organization.auditsTasksData[
              `A-${openedModal.data.id}`
            ];
          const newFinding =
            store.getState().organization.findingsData[findingId]; // Fetch the new finding from the updated state

          if (newFinding) {
            // Update linkedData by appending the new finding
            const updatedLinkedFindings = [
              ...(updatedAuditData.linked_findings || []), // Keep existing linked findings
              newFinding, // Add the newly created finding
            ];

            // updated linkedData in Redux with the new finding
            dispatch(
              setLinkedData({
                ...store.getState().organization.linkedData,
                linked_findings: updatedLinkedFindings, // Append the new finding
              }),
            );
          }

          // Close the new finding modal and reopen the audit modal
          dispatch(hideModal());
          if (updatedAuditData) {
            reopenAuditModal(updatedAuditData); // Reopen the audit modal with updated data
          }
        }, 300);
      } else {
        // If openedModal.data.id is undefined, refresh organization data with different parameters
        await dispatch(
          getOrganizationData(
            orgId,
            sessionToken,
            navigate,
            false,
            true,
            false,
            false,
            false,
            false,
          ),
        );

        dispatch(hideModal());
      }

      actions.resetForm(); // Reset the Formik form
    } catch (error) {
      console.error("Error adding finding:", error);
    } finally {
      setLoading(false);
      actions.setSubmitting(false);
    }
  };

  const handleDescriptionBlur = async (descriptionValue, setFieldValue) => {
    if (!descriptionValue || isFromAuditWithPrefilledRisk) return;

    try {
      const findingsChecks = organizationData?.FindingsChecks || [];
      const params = new URLSearchParams();
      params.append("description", descriptionValue);
      findingsChecks.forEach((q) => params.append("questions", q));

      const response = await axiosInstance.get(
        `/ai/finding_check?${params.toString()}`,
      );

      if (response.data.chapter && response.data.chapter !== "N/A") {
        const chapterLabel = response.data.chapter;
        const chapterId = chapterLabel.split(" ")[0];

        const matchingRisk = cascadingRiskOptions.find((risk) =>
          risk.reference.startsWith(chapterId),
        );

        setPrefilledChapterFromAPI(chapterLabel);

        await setFieldValue("chapter", chapterLabel);
        if (matchingRisk) {
          await setFieldValue("risk", matchingRisk.id);
        }
      }

      if (response.data.checklist) {
        setChecklist(response.data.checklist);
      }
    } catch (error) {
      console.error("Error checking description:", error);
    }
  };

  const ChecklistUI = ({ checklist }) => {
    // Check if all items in the checklist are answered
    const hasUnansweredItems = Object.values(checklist).some(
      (item) => item.check === false,
    );

    // If all items are answered, don't render the checklist
    if (!hasUnansweredItems) return null;

    return (
      <div className={styles.checklistContainer}>
        <div className={styles.title}>Description Improvement Suggestions:</div>
        <ul>
          {Object.entries(checklist).map(([question, { check, answer }]) => (
            <div
              key={question}
              className={classNames(
                styles.list,
                check ? styles.checked : styles.unchecked,
              )}
            >
              <div className={styles.question}>{question} :</div>
              <div className={styles.answer}>
                {check ? "Answered" : "Needs more description"}
              </div>
            </div>
          ))}
        </ul>
      </div>
    );
  };

  // The prefilled chapter value from the modal or API
  const prefilledRisk = openedModal?.data?.risk
    ? `${openedModal.data.risk.risk_id} ${openedModal.data.risk.description}`
    : "";

  const prefilledChapter =
    prefilledChapterFromAPI ||
    (openedModal?.data?.risk
      ? `${openedModal.data.risk.qms_id} ${openedModal.data.risk.qms_description}`
      : "");

  const isFromPrivateAudit = !!openedModal?.data?.private;

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        description: openedModal?.data?.description || "",
        date: "",
        reference: "",
        attachments: [],
        risk: prefilledRiskNum,
        status: "Done",
        chapter: openedModal?.data?.risk
          ? `${openedModal.data.risk.qms_id} ${openedModal.data.risk.qms_description}`
          : "",
        private:
          typeof openedModal?.data?.private === "boolean"
            ? openedModal.data.private
            : false,
      }}
      validationSchema={validationSchema}
      onSubmit={(values, actions) => {
        setLoading(true);
        handleSubmit(values, actions);
      }}
      enableReinitialize={true}
    >
      {({ values, dirty, setFieldValue, resetForm }) => {
        // Extract the chapter id from values.chapter
        const chapterId = values.chapter.split(" ")[0]; // Get the first part of the string before the first space

        // Filter riskOptions based on the extracted chapterId
        const filteredRiskOptions = riskOptions.filter((risk) =>
          risk.reference.startsWith(chapterId),
        );

        const isValidChapterSelected = chapterOptions
          .map((opt) => opt.id)
          .includes(chapterId);

        const selectedRiskObject = cascadingRiskOptions.find(
          (r) => String(r.id) === String(values.risk),
        );
        const initiallyHoveredOption = values.chapter?.split(" ")[0] || null;

        // Filter suboptions (risks) based on the selected chapter
        const suboptionsForChapter = cascadingRiskOptions.filter((opt) =>
          opt.reference.startsWith(chapterId),
        );

        // Show tip only if AI prefilled the chapter and user has more than one option
        const shouldShowRiskSuggestion =
          prefilledChapterFromAPI && suboptionsForChapter.length > 1;

        return (
          <>
            <Modal
              name="new_finding_modal"
              title="New Finding"
              onClose={() => handleCloseModal(dirty, resetForm)}
              width="95vw"
              height="95vh"
            >
              <Form className={styles.content}>
                <div className={styles.description}>
                  <label htmlFor="description">Description</label>
                  <Field
                    name="description"
                    component={Textarea}
                    onBlur={(e) =>
                      handleDescriptionBlur(
                        e.target.value,
                        formikRef.current.setFieldValue,
                      )
                    }
                  />
                </div>
                {checklist && <ChecklistUI checklist={checklist} />}
                <div className={styles.row}>
                  <div className={styles.date}>
                    <label htmlFor="date">Date</label>
                    <Field
                      name="date"
                      component={DatePickerField}
                      validateOnBlur={true}
                      validateOnChange={false}
                    />
                  </div>
                  <div className={styles.reference}>
                    <label htmlFor="reference">Reference</label>
                    <Field name="reference" component={Input} />
                  </div>
                </div>
                <div>
                  <FormikCheckbox
                    name="status"
                    label="Needs follow-up?"
                    trueValue="Open"
                    falseValue="Done"
                  />
                </div>
                <div className={styles.privateContainer}>
                  <FormikCheckbox
                    name="private"
                    label="Make this finding private?"
                    trueValue={true}
                    falseValue={false}
                  />
                  <LockIcon className={styles.lockIcon} />
                  {isFromPrivateAudit && (
                    <>
                      <span
                        className={styles.infoIcon}
                        data-tooltip-id="private-audit-tooltip"
                        data-tooltip-html="This finding is marked as private by default because it originates from a private audit. You may adjust this setting if needed."
                      >
                        i
                      </span>
                      <Tooltip
                        id="private-audit-tooltip"
                        place="top"
                        className={styles.tooltip}
                      />
                    </>
                  )}
                </div>
                <div className={styles.attachment}>
                  <label htmlFor="attachments">Attachments</label>
                  <Field
                    name="attachments"
                    component={FormikUpload}
                    greenIcon
                  />
                </div>
                <div className={styles.chapter}>
                  <Field
                    name="risk"
                    component={CascadingDropdown}
                    label="Associated Chapter and Risk"
                    text="Select a risk"
                    options={cascadingChapterOptions}
                    suboptions={cascadingRiskOptions}
                    disabled={isFromAuditWithPrefilledRisk}
                    selectedValues={[values.risk]}
                    initialHoveredOption={initiallyHoveredOption}
                  />
                </div>
                {shouldShowRiskSuggestion && (
                  <div className={styles.tipMessage}>
                    We've preselected the most relevant risk based on your
                    description. You can choose a more specific one if needed.
                  </div>
                )}
                <Button
                  text={loading ? "Adding..." : "Add New Finding"}
                  type="submit"
                  className={styles.submitButton}
                  disabled={loading}
                />
              </Form>
            </Modal>
            {showConfirmationPopup && (
              <ConfirmationPopup
                message="Your changes won’t be saved if you close this window."
                illustration={WarningIllustration}
                confirmText="Close Without Saving"
                onConfirm={() => handleConfirmClose(resetForm)}
                onCancel={handleCancel}
              />
            )}
          </>
        );
      }}
    </Formik>
  );
};

export default NewFindingModal;
